import { useState, useEffect, useRef } from 'react';
import { getAttachments, uploadAttachments, deleteAttachment } from '../utils/api';
import toast from 'react-hot-toast';

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function getFileIcon(mimeType, name) {
  const ext = name?.split('.').pop()?.toLowerCase();
  if (mimeType?.startsWith('image/')) return '🖼️';
  if (ext === 'pdf') return '📄';
  if (['doc','docx'].includes(ext)) return '📝';
  if (['xls','xlsx'].includes(ext)) return '📊';
  if (['zip','rar'].includes(ext)) return '🗜️';
  return '📎';
}

export default function Attachments({ jobId }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef();

  useEffect(() => {
    if (!jobId) { setLoading(false); return; }
    getAttachments(jobId).then(setFiles).finally(() => setLoading(false));
  }, [jobId]);

  async function handleUpload(e) {
    const selected = Array.from(e.target.files);
    if (!selected.length) return;
    setUploading(true);
    try {
      const uploaded = await uploadAttachments(jobId, selected);
      setFiles(prev => [...uploaded, ...prev]);
      toast.success(`${uploaded.length} file(s) uploaded`);
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  async function handleDelete(file) {
    if (!window.confirm(`Delete ${file.original_name}?`)) return;
    try {
      await deleteAttachment(jobId, file.id);
      setFiles(prev => prev.filter(f => f.id !== file.id));
      toast.success('Deleted');
    } catch {
      toast.error('Delete failed');
    }
  }

  function handleOpen(file) {
    const url = `/api/attachments/${jobId}/file/${file.filename}`;
    window.open(url, '_blank');
  }

  if (!jobId) {
    return (
      <div className="empty-state" style={{ padding: '40px 20px' }}>
        <p style={{ color: 'var(--gray-400)' }}>Save the record first to add attachments.</p>
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          border: '2px dashed var(--gray-300)', borderRadius: 'var(--radius-lg)',
          padding: '28px', textAlign: 'center', marginBottom: 20,
          background: 'var(--gray-50)', cursor: 'pointer',
          transition: 'border-color 0.15s',
        }}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--green-500)'; }}
        onDragLeave={e => { e.currentTarget.style.borderColor = 'var(--gray-300)'; }}
        onDrop={e => {
          e.preventDefault();
          e.currentTarget.style.borderColor = 'var(--gray-300)';
          const dt = { target: { files: e.dataTransfer.files } };
          handleUpload(dt);
        }}
      >
        <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={handleUpload} />
        <div style={{ fontSize: '2rem', marginBottom: 8 }}>📎</div>
        <p style={{ fontWeight: 600, color: 'var(--gray-700)', marginBottom: 4 }}>
          {uploading ? 'Uploading…' : 'Click to upload or drag & drop'}
        </p>
        <p style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>Any file type — max 50MB per file</p>
      </div>

      {loading ? (
        <div className="loader"><div className="spinner" /></div>
      ) : files.length === 0 ? (
        <div className="empty-state" style={{ padding: '24px' }}>
          <p>No files attached yet.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {files.map(file => (
            <div key={file.id} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '10px 14px', background: '#fff',
              border: '1px solid var(--gray-200)', borderRadius: 'var(--radius-md)',
            }}>
              <span style={{ fontSize: '1.4rem', flexShrink: 0 }}>{getFileIcon(file.mime_type, file.original_name)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--gray-800)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {file.original_name}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>
                  {formatBytes(file.file_size)} &nbsp;·&nbsp; {new Date(file.uploaded_at).toLocaleDateString()}
                </div>
              </div>
              <button className="btn btn-outline btn-sm" onClick={() => handleOpen(file)}>View</button>
              <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red-500)' }} onClick={() => handleDelete(file)}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
